import * as FileSaver from "file-saver";
import * as XLSX from "xlsx";
import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Table,
  Input,
  Select,
  Pagination,
  Col,
  Row,
  Flex,
  Tag,
  Tooltip,
  Switch,
} from "antd";
import {
  SearchOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import { Button } from "antd";
import { CSVLink } from "react-csv";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import { capitalizeFirstLetter } from "../../utils/functions";
const { Option } = Select;

// ── Format ISO date strings ───────────────────────────────────────────────────
const formatDate = (val) => {
  if (!val) return "—";
  const str = String(val);
  const isoT = str.match(/^(\d{4})-(\d{2})-(\d{2})T/);
  if (isoT) return `${isoT[3]}/${isoT[2]}/${isoT[1]}`;
  const isoSpace = str.match(/^(\d{4})-(\d{2})-(\d{2})[ T]/);
  if (isoSpace) return `${isoSpace[3]}/${isoSpace[2]}/${isoSpace[1]}`;
  return str;
};

const DataTable = ({
  dataSource,
  columns,
  sumColumns = [],
  onStatusChange,
  backendPagination = false,
  totalRecords = 0,
  page = 1,
  limit = 20,
  onPageChange,
  onSearch,
  onFilterChange,
  loading = false,
  ...rest
}) => {
  const { title, isActive, filterBy, apiBase, ...htmlProps } = rest;
  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filteredData, setFilteredData] = useState(dataSource);
  const [currentRecords, setCurrentRecords] = useState(dataSource);
  const [columnTotals, setColumnTotals] = useState({});

  // ─────────────────────────────────────────────────────────────────────────────
  // FIX: replaced single `statusFilter` + `fieldName` pair with an `activeFilters`
  // map so every SELECT dropdown keeps its own independent key and all active
  // filters are ANDed together instead of the last one overwriting the previous.
  //
  // Shape:  { [fieldName]: value }
  // Reserved key "__status__" is used for the Active / Inactive toggle buttons.
  // `statusFilter` is kept only as a plain-string label for PDF / Excel exports.
  // ─────────────────────────────────────────────────────────────────────────────
  const [activeFilters, setActiveFilters] = useState({});
  const [statusFilter, setStatusFilter] = useState("All"); // export label only

  const dispatch = useDispatch();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);

  const onSearchRef = useRef(onSearch);
  const onFilterChangeRef = useRef(onFilterChange);
  const isFirstRender = useRef(true);

  // Keep refs up-to-date
  useEffect(() => {
    onSearchRef.current = onSearch;
  }, [onSearch]);

  useEffect(() => {
    onFilterChangeRef.current = onFilterChange;
  }, [onFilterChange]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setIsLoading(false);
      } catch (error) {
        setIsLoading(false);
      }
    };
    fetchProducts();
  }, [dispatch]);

  // Debounced search trigger for backend pagination
  useEffect(() => {
    if (!backendPagination || !onSearchRef.current) return;
    if (isFirstRender.current) return;

    const handler = setTimeout(() => {
      onSearchRef.current(searchText);
    }, 400);
    return () => clearTimeout(handler);
  }, [searchText, backendPagination]);

  // Filter change trigger for backend pagination
  useEffect(() => {
    if (!backendPagination || !onFilterChangeRef.current) return;
    if (isFirstRender.current) return;

    onFilterChangeRef.current(activeFilters);
  }, [activeFilters, backendPagination]);

  // Set isFirstRender to false after initial mount
  useEffect(() => {
    isFirstRender.current = false;
  }, []);

  const calculateColumnTotals = (records) => {
    const totals = {};
    if (records) {
      sumColumns.forEach((columnName) => {
        const total = records.reduce(
          (sum, record) => sum + (parseFloat(record[columnName]) || 0),
          0,
        );
        totals[columnName] = total.toFixed(2);
      });
    }
    setColumnTotals(totals);
  };

  const getPagination = useCallback(
    (currentPage, pageSize, filteredRecords) => {
      const pageStart = (currentPage - 1) * pageSize + 1;
      const pageEnd =
        currentPage * pageSize > filteredRecords.length
          ? filteredRecords.length
          : currentPage * pageSize;
      return { pageStart: filteredRecords.length > 0 ? pageStart : 0, pageEnd };
    },
    [],
  );

  const [pageData, setPageData] = useState(
    getPagination(currentPage, pageSize, dataSource),
  );

  const handlePageChange = (page) => setCurrentPage(page);

  const searchTableData = (searchString, data) => {
    return data.filter((item) =>
      Object.values(item).some(
        (value) =>
          value &&
          typeof value === "string" &&
          value.toLowerCase().includes(searchString.toLowerCase()),
      ),
    );
  };

  // FIX: iterates every entry in activeFilters with .every() so a row must
  // satisfy ALL active filters simultaneously (AND logic).
  const applyActiveFilters = (data, filters) => {
    const entries = Object.entries(filters);
    if (entries.length === 0) return data;
    return data.filter((item) =>
      entries.every(([field, value]) => {
        if (field === "__status__") {
          if (value === "Active") return item.status === 1;
          if (value === "Inactive") return item.status === 0;
          return true;
        }
        return item[field] === value;
      }),
    );
  };

  // FIX: merges each new filter into the map under its own key instead of
  // replacing a single shared statusFilter / fieldName pair.
  //   • "All" button (field="")      → returns {} clearing the entire map
  //   • SELECT cleared (value=undef) → deletes only that field's key
  //   • SELECT value chosen          → sets / updates only that field's key
  //   • Active / Inactive buttons    → stored under the reserved "__status__" key
  const handleStatusFilterChange = (value, field = "") => {
    setActiveFilters((prev) => {
      const next = { ...prev };

      if (field === "" || field === "__status__") {
        if (value === "All") return {}; // reset everything
        next["__status__"] = value;
      } else {
        if (value === undefined || value === null) {
          delete next[field]; // user cleared the SELECT → remove its entry
        } else {
          next[field] = value;
        }
      }
      return next;
    });

    // Keep export label in sync
    if (field === "" && value === "All") setStatusFilter("All");
    else if (field === "__status__") setStatusFilter(value);

    setCurrentPage(1);
  };

  useEffect(() => {
    if (backendPagination) {
      setCurrentRecords(dataSource ?? []);
      calculateColumnTotals(dataSource ?? []);
      setFilteredData(dataSource ?? []);

      const pageStart = (page - 1) * limit + 1;
      const pageEnd = Math.min(page * limit, totalRecords);
      setPageData({ pageStart: totalRecords > 0 ? pageStart : 0, pageEnd });
      return;
    }

    const sorted = [...(dataSource ?? [])].sort(
      (a, b) => new Date(b.createdAt) - new Date(a.createdAt), // newest first
    );

    let filtered = searchTableData(searchText, sorted);
    filtered = applyActiveFilters(filtered, activeFilters);

    const sliced = filtered.slice(
      (currentPage - 1) * pageSize,
      currentPage * pageSize,
    );
    setCurrentRecords(sliced);
    calculateColumnTotals(sliced);
    setFilteredData(filtered);
    setPageData(getPagination(currentPage, pageSize, filtered));
  }, [dataSource, searchText, currentPage, pageSize, activeFilters, backendPagination, page, limit, totalRecords]);

  const getCsvData = () =>
    currentRecords.map((item) => {
      const row = {};
      columns.forEach((col) => {
        row[col.dataIndex] = item[col.dataIndex];
      });
      return row;
    });

  // ── PDF EXPORT ──────────────────────────────────────────────────────────────
  const exportToPdf = () => {
    const doc = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a3",
    });
    const pageW = doc.internal.pageSize.getWidth();
    const pageH = doc.internal.pageSize.getHeight();

    const date = new Date();
    const formattedDate = `${String(date.getDate()).padStart(2, "0")}/${String(date.getMonth() + 1).padStart(2, "0")}/${date.getFullYear()}`;
    const title = rest.title || "Data Report";

    const skipKeys = ["action", "isActive"];
    const printCols = columns.filter(
      (col) => !skipKeys.includes(col.dataIndex) && col.title !== "Action",
    );

    const margins = { left: 8, right: 8, top: 26, bottom: 12 };
    const usableW = pageW - margins.left - margins.right;

    const longTextKeys = [
      "description",
      "detail",
      "notes",
      "remark",
      "address",
      "info",
    ];
    const dateKeys = ["at", "date", "on", "created", "updated"];

    const weights = printCols.map((col) => {
      const k = col.dataIndex?.toLowerCase() || "";
      if (longTextKeys.some((x) => k.includes(x))) return 5;
      if (dateKeys.some((x) => k.includes(x))) return 2.2;
      return 1.4;
    });
    const totalWeight = weights.reduce((a, b) => a + b, 0);

    const columnStyles = {};
    printCols.forEach((col, i) => {
      const k = col.dataIndex?.toLowerCase() || "";
      const colW = +((weights[i] / totalWeight) * usableW).toFixed(1);
      columnStyles[i] = {
        cellWidth: colW,
        halign: longTextKeys.some((x) => k.includes(x)) ? "left" : "center",
        overflow: "linebreak",
      };
    });

    const rows = currentRecords.map((item) =>
      printCols.map((col) => {
        const val = item[col.dataIndex];
        if (val === undefined || val === null || val === "") return "—";
        const k = col.dataIndex?.toLowerCase() || "";
        if (dateKeys.some((x) => k.includes(x))) return formatDate(val);
        if (k === "status")
          return val === 1 ? "Active" : val === 0 ? "Inactive" : String(val);
        if (typeof val === "boolean") return val ? "Yes" : "No";
        if (val === "true") return "Yes";
        if (val === "false") return "No";
        return String(val);
      }),
    );

    const hasTotals =
      sumColumns.length > 0 && Object.keys(columnTotals).length > 0;
    if (hasTotals) {
      rows.push(
        printCols.map((col, i) => {
          if (sumColumns.includes(col.dataIndex) && columnTotals[col.dataIndex])
            return columnTotals[col.dataIndex];
          return i === 0 ? "TOTAL" : "";
        }),
      );
    }

    doc.setFillColor(59, 91, 219);
    doc.rect(0, 0, pageW, 20, "F");
    doc.setFontSize(13);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(255, 255, 255);
    doc.text(title, margins.left, 13);
    doc.setFontSize(7.5);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(180, 200, 255);
    doc.text(
      `Generated: ${formattedDate}   ·   Records: ${currentRecords.length}   ·   Filter: ${statusFilter}`,
      margins.left,
      18,
    );

    doc.autoTable({
      head: [printCols.map((col) => col.title)],
      body: rows,
      startY: margins.top,
      margin: {
        left: margins.left,
        right: margins.right,
        bottom: margins.bottom,
      },
      tableWidth: usableW,
      columnStyles,
      styles: {
        font: "helvetica",
        fontSize: 7,
        cellPadding: { top: 3, right: 3, bottom: 3, left: 3 },
        valign: "top",
        overflow: "linebreak",
        lineColor: [210, 218, 238],
        lineWidth: 0.18,
        textColor: [35, 40, 52],
        minCellHeight: 7,
        maxCellHeight: 28,
      },
      headStyles: {
        fillColor: [59, 91, 219],
        textColor: [255, 255, 255],
        fontStyle: "bold",
        fontSize: 7.5,
        halign: "center",
        valign: "middle",
        cellPadding: { top: 4, right: 3, bottom: 4, left: 3 },
        lineWidth: 0,
        minCellHeight: 10,
      },
      alternateRowStyles: { fillColor: [245, 247, 255] },
      bodyStyles: { fillColor: [255, 255, 255] },
      didParseCell(data) {
        if (
          hasTotals &&
          data.section === "body" &&
          data.row.index === rows.length - 1
        ) {
          data.cell.styles.fillColor = [219, 228, 255];
          data.cell.styles.fontStyle = "bold";
          data.cell.styles.textColor = [30, 60, 180];
        }
      },
      didDrawPage(data) {
        const totalPages = doc.internal.getNumberOfPages();
        doc.setFontSize(7);
        doc.setTextColor(160, 160, 175);
        const footerY = pageH - 5;
        doc.text(
          `Page ${data.pageNumber} of ${totalPages}`,
          pageW / 2,
          footerY,
          { align: "center" },
        );
        doc.text(title, margins.left, footerY);
        doc.text(formattedDate, pageW - margins.right, footerY, {
          align: "right",
        });
      },
    });

    doc.save(
      `${title.replace(/\s+/g, "_")}_${formattedDate.replace(/\//g, "-")}.pdf`,
    );
  };

  // ── EXCEL EXPORT ────────────────────────────────────────────────────────────
  const exportToExcel = () => {
    const date = new Date();
    const formattedDate = `${date.getDate()}-${date.getMonth() + 1}-${date.getFullYear()}`;
    const title = rest.title || "Data Report";
    const dataRows = currentRecords.map((record) =>
      columns.reduce((obj, column) => {
        const val = record[column.dataIndex];
        obj[column.title] = val !== undefined && val !== null ? val : "";
        return obj;
      }, {}),
    );
    const hasTotals =
      sumColumns.length > 0 && Object.keys(columnTotals).length > 0;
    if (hasTotals) {
      dataRows.push(
        columns.reduce((obj, column) => {
          if (
            sumColumns.includes(column.dataIndex) &&
            columnTotals[column.dataIndex]
          )
            obj[column.title] = parseFloat(columnTotals[column.dataIndex]);
          else obj[column.title] = column === columns[0] ? "TOTAL" : "";
          return obj;
        }, {}),
      );
    }
    const ws = XLSX.utils.json_to_sheet(dataRows);
    ws["!cols"] = columns.map((col) => ({
      wch: Math.min(
        Math.max(
          col.title.length + 4,
          ...currentRecords.map(
            (r) => String(r[col.dataIndex] ?? "").length + 2,
          ),
          12,
        ),
        40,
      ),
    }));
    ws["!freeze"] = {
      xSplit: 0,
      ySplit: 1,
      topLeftCell: "A2",
      activePane: "bottomLeft",
    };
    for (let c = 0; c < columns.length; c++) {
      const cellAddr = XLSX.utils.encode_cell({ r: 0, c });
      if (!ws[cellAddr]) continue;
      ws[cellAddr].s = {
        font: { bold: true, color: { rgb: "FFFFFF" }, name: "Arial", sz: 11 },
        fill: { fgColor: { rgb: "3B5BDB" }, patternType: "solid" },
        alignment: { horizontal: "center", vertical: "center", wrapText: true },
        border: { bottom: { style: "medium", color: { rgb: "2244BB" } } },
      };
    }
    const rowCount = dataRows.length;
    for (let r = 1; r <= rowCount; r++) {
      const isTotalsRow = hasTotals && r === rowCount;
      for (let c = 0; c < columns.length; c++) {
        const cellAddr = XLSX.utils.encode_cell({ r, c });
        if (!ws[cellAddr]) ws[cellAddr] = { t: "s", v: "" };
        ws[cellAddr].s = {
          font: {
            name: "Arial",
            sz: 10,
            bold: isTotalsRow,
            color: { rgb: isTotalsRow ? "1E3CB4" : "1E1E1E" },
          },
          fill: {
            fgColor: {
              rgb: isTotalsRow ? "DBE4FF" : r % 2 === 0 ? "F5F7FF" : "FFFFFF",
            },
            patternType: "solid",
          },
          alignment: { vertical: "center" },
          border: {
            top: { style: "thin", color: { rgb: "DCE1EB" } },
            bottom: { style: "thin", color: { rgb: "DCE1EB" } },
            left: { style: "thin", color: { rgb: "DCE1EB" } },
            right: { style: "thin", color: { rgb: "DCE1EB" } },
          },
        };
      }
    }
    const metaWs = XLSX.utils.aoa_to_sheet([
      ["Report Title", title],
      ["Export Date", formattedDate],
      ["Total Records", currentRecords.length],
      ["Filter", statusFilter !== "All" ? statusFilter : "None"],
    ]);
    metaWs["!cols"] = [{ wch: 18 }, { wch: 30 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Data");
    XLSX.utils.book_append_sheet(wb, metaWs, "Info");
    const excelBuffer = XLSX.write(wb, {
      bookType: "xlsx",
      type: "array",
      cellStyles: true,
    });
    FileSaver.saveAs(
      new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
      }),
      `${title.replace(/\s+/g, "_")}_${formattedDate}.xlsx`,
    );
  };

  // ── TABLE COLUMN RENDERING ──────────────────────────────────────────────────
  const longTextKeys = [
    "description",
    "detail",
    "notes",
    "remark",
    "address",
    "info",
  ];
  const dateKeys = ["at", "date", "on"];

  const updatedColumns = columns.map((column) => {
    const baseTitle =
      sumColumns.includes(column.dataIndex) && columnTotals[column.dataIndex]
        ? `${column.title} (Total: ${columnTotals[column.dataIndex]})`
        : column.title;
    const base = { ...column, title: baseTitle };
    const k = column.dataIndex?.toLowerCase() || "";

    if (column.dataIndex === "status" || column.key === "status") {
      return {
        title: baseTitle,
        dataIndex: "status",
        key: "status",
        width: 110,
        align: "center",
        render: (val, record) => (
          <Switch
            checked={val === 1 || val === true}
            onChange={() => onStatusChange?.(record.id, val)}
            style={{
              backgroundColor:
                val === 1 || val === true ? "#2f9e44" : "#adb5bd",
              minWidth: 40,
            }}
          />
        ),
      };
    }

    if (longTextKeys.some((x) => k.includes(x))) {
      return {
        ...base,
        width: 220,
        ellipsis: false,
        render: (text) =>
          text ? (
            <Tooltip
              title={text}
              placement="topLeft"
              overlayStyle={{ maxWidth: 360 }}
            >
              <div
                style={{
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  lineHeight: "1.5",
                  fontSize: "0.81rem",
                  color: "#495057",
                  cursor: "default",
                  maxWidth: 220,
                }}
              >
                {text}
              </div>
            </Tooltip>
          ) : (
            <span style={{ color: "#ced4da" }}>—</span>
          ),
      };
    }

    if (dateKeys.some((x) => k.includes(x))) {
      return {
        ...base,
        width: 105,
        align: "center",
        render: (val) => (
          <span
            style={{
              fontSize: "0.8rem",
              color: "#495057",
              whiteSpace: "nowrap",
            }}
          >
            {formatDate(val)}
          </span>
        ),
      };
    }

    if (k === "demo" || k === "isdemo") {
      return {
        ...base,
        width: 75,
        align: "center",
        render: (val) => {
          const isYes = val === true || val === "true";
          return (
            <Tag
              style={{
                borderRadius: 20,
                fontSize: "0.68rem",
                padding: "1px 8px",
                background: isYes ? "#e7f5ff" : "#f8f9fa",
                color: isYes ? "#1971c2" : "#868e96",
                border: `1px solid ${isYes ? "#a5d8ff" : "#dee2e6"}`,
                fontWeight: 600,
                margin: 0,
              }}
            >
              {isYes ? "Yes" : "No"}
            </Tag>
          );
        },
      };
    }

    if (k === "action" || base.title === "Action") {
      return { ...base, width: 85, align: "center", fixed: "right" };
    }

    if (k === "name") {
      return {
        ...base,
        width: 120,
        render: (val) => (
          <span
            style={{ fontWeight: 600, color: "#343a40", fontSize: "0.84rem" }}
          >
            {val || "—"}
          </span>
        ),
      };
    }

    const shortKeys2 = [
      "discount",
      "gross",
      "amc",
      "period",
      "type",
      "business",
    ];
    if (shortKeys2.some((x) => k.includes(x))) {
      return { ...base, width: 100, align: "center" };
    }

    return { ...base, align: "center" };
  });

  return (
    <div {...htmlProps}>
      <style>{`
        .dt-toolbar {
          background: #ffffff;
          border: 1px solid #e4e8f0;
          border-bottom: none;
          border-radius: 12px 12px 0 0;
          padding: 14px 18px 12px;
        }
        .dt-table-wrap .ant-table {
          font-size: 0.83rem;
          border-left: 1px solid #e4e8f0;
          border-right: 1px solid #e4e8f0;
        }
        .dt-table-wrap .ant-table-thead > tr > th {
          background: #f0f3ff !important;
          color: #3b5bdb !important;
          font-weight: 700 !important;
          font-size: 0.72rem !important;
          letter-spacing: 0.05em;
          text-transform: uppercase;
          padding: 10px 12px !important;
          border-bottom: 2px solid #c5d0fa !important;
          white-space: nowrap;
        }
        .dt-table-wrap .ant-table-thead > tr > th::before { display: none !important; }
        .dt-table-wrap .ant-table-tbody > tr > td {
          padding: 8px 12px !important;
          border-bottom: 1px solid #eef0f8 !important;
          vertical-align: middle !important;
          transition: background 0.1s;
        }
        .dt-table-wrap .ant-table-tbody > tr:nth-child(even) > td { background: #f7f8ff; }
        .dt-table-wrap .ant-table-tbody > tr:nth-child(odd) > td  { background: #ffffff; }
        .dt-table-wrap .ant-table-tbody > tr:hover > td { background: #e8edff !important; }
        .dt-table-wrap .ant-table-cell-fix-right {
          box-shadow: -3px 0 10px rgba(59,91,219,0.06) !important;
        }
        .dt-table-wrap .ant-table-tbody > tr:hover .ant-table-cell-fix-right { background: #e8edff !important; }
        .dt-table-wrap .ant-table-tbody > tr:nth-child(even) .ant-table-cell-fix-right { background: #f7f8ff; }
        .dt-table-wrap .ant-table-tbody > tr:nth-child(odd)  .ant-table-cell-fix-right { background: #ffffff; }
        .dt-table-wrap .ant-table-column-sorter-up.active,
        .dt-table-wrap .ant-table-column-sorter-down.active { color: #3b5bdb; }
        .dt-pagination-bar {
          background: #ffffff;
          border: 1px solid #e4e8f0;
          border-top: 1px solid #eef0f8;
          border-radius: 0 0 12px 12px;
          padding: 10px 18px;
        }
        .dt-pagination-bar .ant-pagination-item-active {
          border-color: #3b5bdb !important;
          background: #3b5bdb !important;
          border-radius: 6px;
        }
        .dt-pagination-bar .ant-pagination-item-active a { color: #fff !important; }
        .dt-pagination-bar .ant-pagination-item { border-radius: 6px; }
        .dt-pagination-bar .ant-pagination-item:hover { border-color: #3b5bdb; }
        .dt-pagination-bar .ant-pagination-item:hover a { color: #3b5bdb; }
        .dt-export-btn {
          height: 30px !important; padding: 0 13px !important;
          font-size: 0.74rem !important; font-weight: 700 !important;
          border-radius: 7px !important; letter-spacing: 0.04em;
          display: inline-flex !important; align-items: center !important;
          gap: 5px; border: none !important;
          box-shadow: 0 2px 6px rgba(0,0,0,0.12) !important;
          transition: opacity 0.15s, transform 0.1s !important;
        }
        .dt-export-btn:hover { opacity: 0.88 !important; transform: translateY(-1px); }
        .dt-export-csv { background: #099268 !important; }
        .dt-export-xl  { background: #1864ab !important; }
        .dt-export-pdf { background: #c92a2a !important; }
        .dt-filter-pill {
          height: 28px !important; font-size: 0.72rem !important;
          font-weight: 700 !important; border-radius: 20px !important;
          padding: 0 14px !important; letter-spacing: 0.04em;
          border: none !important; box-shadow: 0 1px 4px rgba(0,0,0,0.1) !important;
        }
        .dt-search .ant-input-affix-wrapper {
          border-radius: 8px !important; border: 1px solid #dee2e6 !important;
          font-size: 0.82rem !important;
          transition: border-color 0.15s, box-shadow 0.15s !important;
        }
        .dt-search .ant-input-affix-wrapper:focus-within {
          border-color: #3b5bdb !important;
          box-shadow: 0 0 0 3px rgba(59,91,219,0.12) !important;
        }
        .dt-search .ant-input { font-size: 0.82rem !important; }
      `}</style>

      {/* ── TOOLBAR ── */}
      <div className="dt-toolbar">
        <Row gutter={[10, 10]} align="middle">
          <Col xs={24} sm={14} md={16}>
            <Flex align="center" gap={8}>
              <span
                style={{
                  fontSize: "0.79rem",
                  color: "#868e96",
                  whiteSpace: "nowrap",
                }}
              >
                Show
              </span>
              <Select
                defaultValue={10}
                size="small"
                onChange={(value) => {
                  setPageSize(value === 0 ? dataSource.length : value);
                  setCurrentPage(1);
                }}
                style={{ width: 68 }}
              >
                <Option value={10}>10</Option>
                <Option value={20}>20</Option>
                <Option value={30}>30</Option>
                <Option value={40}>40</Option>
                <Option value={0}>All</Option>
              </Select>
              <span style={{ fontSize: "0.79rem", color: "#868e96" }}>
                entries
              </span>
            </Flex>
          </Col>

          <Col xs={24} sm={10} md={8}>
            <Input
              className="dt-search"
              placeholder="Search records…"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              suffix={
                <SearchOutlined style={{ color: "#adb5bd", fontSize: 13 }} />
              }
              size="small"
            />
          </Col>

          <Col xs={24}>
            <Flex justify="space-between" align="center" wrap="wrap" gap={8}>
              <Flex gap={6} wrap="wrap" align="center">
                {location.pathname !== "/glGroup" && (
                  <>
                    {/* FIX: field="" signals handleStatusFilterChange to clear all filters */}
                    <Button
                      type="primary"
                      size="small"
                      className="dt-filter-pill"
                      style={{ background: "#7048e8" }}
                      onClick={() => handleStatusFilterChange("All", "")}
                    >
                      All
                    </Button>

                    {rest.filterBy?.length > 0 &&
                      rest.filterBy.map((f, i) => {
                        if (f.fieldType === "SELECT")
                          return (
                            <Select
                              key={i}
                              size="small"
                              placeholder={capitalizeFirstLetter(f.label)}
                              allowClear
                              showSearch
                              style={{ minWidth: 130 }}
                              filterOption={(input, option) =>
                                option.children
                                  .toLowerCase()
                                  .indexOf(input.toLowerCase()) >= 0
                              }
                              // FIX: each SELECT writes to its own f.field key in the
                              // map; undefined (user cleared) removes only that key
                              onChange={(e) =>
                                handleStatusFilterChange(e, f.field)
                              }
                            >
                              {f.options.map((opt) => (
                                <Select.Option key={opt.id} value={opt.id}>
                                  {opt.name}
                                </Select.Option>
                              ))}
                            </Select>
                          );
                        return (
                          <Button
                            key={i}
                            type="primary"
                            size="small"
                            className="dt-filter-pill"
                            style={{ background: "#2f9e44" }}
                            onClick={() =>
                              handleStatusFilterChange(f.filter, f.field)
                            }
                          >
                            {f.filter}
                          </Button>
                        );
                      })}

                    {rest.isActive && (
                      <>
                        {/* FIX: Active / Inactive now use the reserved "__status__" key */}
                        <Button
                          type="primary"
                          size="small"
                          className="dt-filter-pill"
                          style={{ background: "#2f9e44" }}
                          onClick={() =>
                            handleStatusFilterChange("Active", "__status__")
                          }
                        >
                          Active
                        </Button>
                        <Button
                          type="primary"
                          size="small"
                          className="dt-filter-pill"
                          style={{ background: "#c92a2a" }}
                          onClick={() =>
                            handleStatusFilterChange("Inactive", "__status__")
                          }
                        >
                          Inactive
                        </Button>
                      </>
                    )}
                  </>
                )}
              </Flex>

              <Flex gap={6}>
                <CSVLink data={getCsvData()} filename={"data.csv"}>
                  <Button
                    type="primary"
                    size="small"
                    className="dt-export-btn dt-export-csv"
                  >
                    <FileTextOutlined /> CSV
                  </Button>
                </CSVLink>
                <Button
                  type="primary"
                  size="small"
                  className="dt-export-btn dt-export-xl"
                  onClick={exportToExcel}
                >
                  <FileExcelOutlined /> Excel
                </Button>
                <Button
                  type="primary"
                  size="small"
                  className="dt-export-btn dt-export-pdf"
                  onClick={exportToPdf}
                >
                  <FilePdfOutlined /> PDF
                </Button>
              </Flex>
            </Flex>
          </Col>
        </Row>
      </div>

      {/* ── TABLE ── */}
      <div className="dt-table-wrap">
        <Table
          dataSource={currentRecords}
          columns={updatedColumns}
          pagination={false}
          rowKey={(record) => record.key}
          scroll={{ x: "max-content" }}
          size="small"
          loading={loading}
          exportable
          exportableProps={{ showColumnPicker: true }}
        />
      </div>

      {/* ── PAGINATION ── */}
      <div className="dt-pagination-bar">
        <Row gutter={16} align="middle">
          <Col span={12}>
            <span style={{ fontSize: "0.79rem", color: "#868e96" }}>
              Showing{" "}
              <strong style={{ color: "#495057" }}>
                {backendPagination ? (pageData?.pageStart ?? 0) : pageData?.pageStart}
              </strong>
              {" – "}
              <strong style={{ color: "#495057" }}>{backendPagination ? (pageData?.pageEnd ?? 0) : pageData?.pageEnd}</strong>
              {" of "}
              <strong style={{ color: "#3b5bdb" }}>
                {backendPagination ? totalRecords : filteredData.length}
              </strong>
              {" entries"}
            </span>
          </Col>
          <Col span={12} style={{ textAlign: "right" }}>
            <Pagination
              current={backendPagination ? page : currentPage}
              pageSize={backendPagination ? limit : pageSize}
              total={backendPagination ? totalRecords : filteredData.length}
              onChange={backendPagination ? onPageChange : handlePageChange}
              showSizeChanger={false}
              size="small"
            />
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default DataTable;
